import { useCallback } from 'react';
import { isMobile } from 'react-device-detect';

import { useUser } from 'context/userContext';

import { intercomBootSettings } from 'utils/identity/trackingIdentity';
import useCurrentPage from 'utils/hooks/useCurrentPage';
import { isLocalhost } from 'utils/urlUtils';

export const useIntercom = () => {
  const { userDetails, modules, moduleProjects } = useUser();
  const { isGkp, isSocialMonitoring, isInsights, isInsightsCapture } = useCurrentPage();

  const initiateIntercom = useCallback(async () => {
    const intercomLauncher = document.getElementsByClassName('intercom-launcher')?.[0];
    const intercomOpened = document.getElementsByClassName('intercom-app')?.[0];
    const { Intercom } = window;
    const debugMode = import.meta.env.VITE_INTERCOM_DEBUG === 'true';
    const shouldInitialize =
      userDetails?.email &&
      (debugMode || !isLocalhost()) &&
      !isMobile &&
      (isGkp || isSocialMonitoring || isInsights || isInsightsCapture);

    if (shouldInitialize) {
      const currentModule = modules?.find(module => module.isCurrent);
      const currentProject = currentModule
        ? moduleProjects(currentModule.key)?.find(
            project => project.projectID === currentModule.currentProject,
          )
        : undefined;
      window.intercomSettings = {
        app_id: 'y588ttna',
        custom_launcher_selector: '#intercom_help_center',
      };
      // #194/#23: the API supplies the id and its signature; nothing is signed here.
      Intercom(
        'boot',
        intercomBootSettings({ userDetails, currentModule, currentProject, now: Date.now() }),
      );
    } else if (Intercom) {
      // Hide launcher when conditions aren't met
      Intercom('update', { hide_default_launcher: true });
    }
    // Legacy DOM manipulation for edge cases
    if (intercomLauncher) {
      intercomLauncher.style.visibility = shouldInitialize ? 'visible' : 'hidden';
    }
    if (intercomOpened) {
      intercomOpened.style.visibility = shouldInitialize ? 'visible' : 'hidden';
    }
  }, [
    userDetails,
    isGkp,
    isSocialMonitoring,
    isInsights,
    isInsightsCapture,
    modules,
    moduleProjects,
  ]);

  return { initiateIntercom };
};
